import { useMemo } from 'react';
import { useLocation } from 'react-router';
import { useTracker } from '@archipro-website/tracker';
import { reportFilterApplied } from '@modules-rocco/filters/tracking/report-filter-applied';
import {
    DirectoryFilterSidebar,
    type CategoryItem,
    type LeadingFilterGroup,
} from '@ui-rocco/pages/directory-browse/DirectoryFilterSidebar';
import {
    DirectoryBrowseThemeProvider,
    type DirectoryBrowseTheme,
} from '@ui-rocco/pages/directory-browse/directory-browse-theme';
import { useProfessionalTabFilterSort } from '@modules/professional/hook/use-professional-tab-filter-sort';
import { PROFESSIONAL_SECTIONS_ANCHOR } from '@modules/professional/util/constants';
import type { FiltersConfig } from '@rocco/ui/filter-sort/types/filter-config-section';
import type {
    FilterStates,
    FilterStatesKey,
} from '@modules-rocco/filters/types/filter-states';
import type {
    CategoryFilterState,
    CategoryTree,
} from '@modules-rocco/filters/filter-state/category-filter-state';
import type { SearchIndexes } from '@modules-rocco/search/types/enum/search-indexes';
import type { SearchServiceQueryFilters } from '@modules-rocco/search/types/api';

interface ProfessionalTabFilterSidebarProps {
    searchIndex: SearchIndexes;
    config: FiltersConfig;
    /** The tab loader's `filterStatesRocco`. */
    initialFilterStates: FilterStates;
    theme?: DirectoryBrowseTheme;
    extraIgnoreFilterKeys?: FilterStatesKey[];
    defaultViewProjectsBy?: string;
    /** The tab loader's `aggregateScopeFilters` (ID-based scopes). */
    aggregateScopeFilters?: SearchServiceQueryFilters;
    /** Group rendered above the config's sections (the Products brand family). */
    leadingGroup?: LeadingFilterGroup;
}

const findParent = (
    root: CategoryTree,
    target: CategoryTree,
): CategoryTree | null => {
    if (root.children.some((child) => child.url === target.url)) {
        return root;
    }
    for (const child of root.children) {
        const found = findParent(child, target);
        if (found) {
            return found;
        }
    }
    return null;
};

/**
 * One drill-down level of the professional-scoped category tree: the selected
 * node's children (or its siblings at a leaf), plus a "Back" row to the
 * selected category's parent once a category is applied. Links stay on the
 * tab pathname with `categoryLink` query params, keeping the other applied
 * filters intact.
 */
export const buildCategoryItems = (
    state: CategoryFilterState,
    pathname: string,
    search: string,
): CategoryItem[] => {
    const selected =
        !state.isRoot && state.selectedCategory ? state.selectedCategory : null;
    const parent = selected ? findParent(state.category, selected) : null;
    const listParent = selected
        ? selected.children.length
            ? selected
            : (parent ?? state.category)
        : state.category;

    if (!listParent.children.length) {
        return [];
    }

    const linkFor = (categoryLink: string | null) => {
        const params = new URLSearchParams(search);
        params.delete('page');
        if (categoryLink) {
            params.set('categoryLink', categoryLink);
        } else {
            params.delete('categoryLink');
        }
        const query = params.toString();
        return `${pathname}${query ? `?${query}` : ''}#${PROFESSIONAL_SECTIONS_ANCHOR}`;
    };

    // What the reader actually chose, as opposed to the category the tab is
    // scoped to. A profile with one category arrives with it selected in the
    // search state and no `categoryLink` in the address bar.
    const chosenCategoryLink = new URLSearchParams(search).get('categoryLink');

    const items: CategoryItem[] = listParent.children.map((child) => ({
        id: child.url,
        title: child.title,
        link: linkFor(child.url),
        isActive: child.url === selected?.url,
        isCurrent: child.url === chosenCategoryLink,
        hasChildren: child.children.length > 0,
    }));

    if (selected) {
        // Back = the selected category's parent; at the top level (or for a
        // parent the tree can't address by URL) that is the professional's
        // root tree, which maps to no categoryLink at all.
        const backToRoot = !parent || parent.root || !parent.url;
        items.unshift({
            id: 'back',
            title: 'Back',
            link: linkFor(backToRoot ? null : parent.url),
            ariaLabel: backToRoot
                ? 'Back to all categories'
                : `Back to ${parent.title}`,
            isActive: false,
            isBack: true,
        });
    }

    return items;
};

/**
 * The Rocco filter sidebar wired for a professional list tab: the tab's own
 * filter config and URL format, themed via the directory browse theme (the
 * case-studies tab is dark on desktop).
 */
export const ProfessionalTabFilterSidebar = ({
    searchIndex,
    config,
    initialFilterStates,
    theme = 'light',
    extraIgnoreFilterKeys,
    defaultViewProjectsBy,
    aggregateScopeFilters,
    leadingGroup,
}: ProfessionalTabFilterSidebarProps) => {
    const location = useLocation();

    const { searchFilters, sortOptions } = useProfessionalTabFilterSort({
        config,
        searchIndex,
        initialFilterStates,
        extraIgnoreFilterKeys,
        defaultViewProjectsBy,
        aggregateScopeFilters,
    });

    const categoryState = initialFilterStates.states.category;
    const categories = useMemo(
        () =>
            categoryState
                ? buildCategoryItems(
                      categoryState,
                      location.pathname,
                      location.search,
                  )
                : undefined,
        [categoryState, location.pathname, location.search],
    );

    const tracker = useTracker();
    const reportCategoryFilter = (category: CategoryItem) =>
        reportFilterApplied(
            tracker,
            location.pathname + location.search,
            category.link,
            true,
            'professionalTab',
        );

    return (
        <DirectoryBrowseThemeProvider value={theme}>
            <DirectoryFilterSidebar
                searchFilters={searchFilters}
                sortOptions={sortOptions}
                categories={categories}
                // Tab filters, not category moves: picking one applies a filter.
                onCategoryClick={reportCategoryFilter}
                hiddenSectionTitles={[]}
                // The tab layout owns the page gutter, so drop the browse
                // aside's offset.
                //
                // The bottom pad is room to scroll past the staff tools, which
                // float at `bottom-8` over this panel's lower rows (Brand and
                // Designer on a long config). The panel's own bottom edge sits
                // 1.5rem off the viewport, and the pill reaches about 5.5rem up
                // from it, so 6rem of scroll clears it. Desktop only, which is
                // the only place those tools render. Before the revamp the
                // professional filters lived in a drawer and nothing collided.
                className="md:ml-0 md:pb-24"
                leadingGroups={leadingGroup ? [leadingGroup] : undefined}
            />
        </DirectoryBrowseThemeProvider>
    );
};
